import React, { useState } from 'react';
import { productService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

function ProductImport() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [result, setResult] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.name.toLowerCase().endsWith('.xlsx')) {
        setError('Por favor seleccione un archivo Excel (.xlsx)');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setErrorTrace(null);
      setResult(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Por favor seleccione un archivo');
      return;
    }

    setLoading(true);
    setError('');
    setErrorTrace(null);
    setResult(null);

    try {
      const response = await productService.importProducts(file);
      
      if (response.success) {
        setResult(response.data);
        setFile(null);
        // Reset file input by changing key
        setFileInputKey(Date.now());
      } else {
        setError(response.message || 'Error al importar productos');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al importar productos');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-import">
      <h2>Importar Productos desde Excel</h2>
      
      <div className="import-instructions">
        <h3>Formato del archivo Excel</h3>
        <p>El archivo Excel debe tener las siguientes columnas (en este orden):</p>
        <table className="format-table">
          <thead>
            <tr>
              <th>Columna</th>
              <th>Nombre</th>
              <th>Descripción</th>
              <th>Requerido</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>A</td>
              <td>Código de Barras</td>
              <td>Código de barras del producto (único)</td>
              <td>Sí</td>
            </tr>
            <tr>
              <td>B</td>
              <td>Código Externo</td>
              <td>Código externo del producto (opcional)</td>
              <td>No</td>
            </tr>
            <tr>
              <td>C</td>
              <td>Nombre</td>
              <td>Nombre del producto</td>
              <td>Sí</td>
            </tr>
          </tbody>
        </table>
        
        <div className="import-notes">
          <h4>Notas importantes:</h4>
          <ul>
            <li>La primera fila debe contener los encabezados de las columnas</li>
            <li>Si un producto ya existe (mismo código de barras), no se modificará</li>
            <li>Los productos nuevos se crearán automáticamente</li>
            <li>El archivo debe tener formato .xlsx (Excel)</li>
          </ul>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="import-form">
        <div className="input-group">
          <label htmlFor="fileInput">Seleccionar archivo Excel</label>
          <input
            type="file"
            id="fileInput"
            key={fileInputKey}
            accept=".xlsx"
            onChange={handleFileChange}
            disabled={loading}
          />
          {file && (
            <p className="file-selected">
              Archivo seleccionado: <strong>{file.name}</strong>
            </p>
          )}
        </div>

        <ErrorDisplay error={error} errorTrace={errorTrace} />

        {result && (
          <div className="import-result">
            <h3>Resultado de la Importación</h3>
            <div className="result-summary">
              <div className="result-item">
                <span className="result-label">Total de filas procesadas:</span>
                <span className="result-value">{result.totalRows}</span>
              </div>
              <div className="result-item success">
                <span className="result-label">Productos creados:</span>
                <span className="result-value">{result.created}</span>
              </div>
              <div className="result-item info">
                <span className="result-label">Productos existentes (omitidos):</span>
                <span className="result-value">{result.existing}</span>
              </div>
              <div className="result-item error">
                <span className="result-label">Errores:</span>
                <span className="result-value">{result.errors}</span>
              </div>
            </div>

            {result.errorMessages && result.errorMessages.length > 0 && (
              <div className="error-messages">
                <h4>Detalle de Errores:</h4>
                <ul>
                  {result.errorMessages.map((msg, idx) => (
                    <li key={idx}>{msg}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading || !file}
          >
            {loading ? 'Importando...' : 'Importar Productos'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ProductImport;
